import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { downloadCanvasWithWatermark } from "../chartWatermark";
import { formatNumberWithSeparators, toNumber } from "../utils/formatting";

// Color scheme for Balance Sheet
const BALANCE_COLORS = {
  ASSETS: "#18AD86",
  LIABILITIES: "#EA8000",
};

export interface BalanceDataPoint {
  section: string;
  category: string;
  value: string | number;
  valueFmt?: string | number;
}

export interface BalanceSheetData {
  dataPoints?: BalanceDataPoint[];
}

export interface BalanceSankeyRef {
  downloadImage: () => void;
}

interface BalanceSankeyProps {
  data?: BalanceSheetData;
  currencyFormatter?: (value: number) => string;
  currencySymbol?: string;
  decimalPlaces?: number;
  unidades?: "K" | "M" | "B";
  divisa?: string;
}

interface NodePosition {
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  value: number;
  percentage: number;
  color: string;
  side: "left" | "center" | "right";
}

interface FlowPosition {
  path: Path2D;
  sourceName: string;
  targetName: string;
  value: number;
  percentage: number;
  color: string;
}

interface TooltipData {
  x: number;
  y: number;
  name: string;
  value: string;
  percentage: string;
  color: string;
  isFlow?: boolean;
}

const ChartContainer = styled(Box)({
  position: "relative",
  width: "100%",
  backgroundColor: "#ffffff",
});

const DivisaBadge = styled(Box)({
  position: "absolute",
  top: 12,
  right: 12,
  backgroundColor: "#F8FAFC",
  padding: "6px 16px",
  borderRadius: "6px",
  fontSize: "13px",
  fontFamily: '"Open Sans", sans-serif',
  color: "#64748B",
  fontWeight: 600,
  border: "1px solid #E2E8F0",
  zIndex: 10,
});

const Tooltip = styled(Box, {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ visible }) => ({
  position: "absolute",
  backgroundColor: "#FFFFFF",
  color: "#1e293b",
  padding: "12px 16px",
  borderRadius: 8,
  fontSize: "14px",
  fontFamily: '"Open Sans", sans-serif',
  pointerEvents: "none",
  zIndex: 100,
  opacity: visible ? 1 : 0,
  transition: "opacity 0.15s ease-out",
  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  minWidth: 100,
}));

const TooltipLabel = styled("div")({
  fontWeight: 600,
  marginBottom: 6,
  color: "#1e293b",
  fontSize: "14px",
});

const TooltipValue = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: "14px",
});

const TooltipColorDot = styled("div")<{ color: string }>(({ color }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: color,
}));

const defaultData: BalanceSheetData = {
  dataPoints: [],
};

const createCurrencyFormatter =
  (symbol: string, decimals: number = 0, unidades: "K" | "M" | "B" = "K") =>
  (value: number): string => {
    // Backend already returns values in the selected unit; no extra scaling needed
    const displayValue = value;
    const suffix = unidades;
    return `${symbol}${formatNumberWithSeparators(
      displayValue,
      decimals,
    )}${suffix}`;
  };

export const BalanceSankey = forwardRef<BalanceSankeyRef, BalanceSankeyProps>(
  (
    {
      data = defaultData,
      currencyFormatter,
      currencySymbol = "$",
      decimalPlaces = 0,
      unidades = "K",
      divisa = "",
    },
    ref,
  ) => {
    const effectiveCurrencyFormatter = useMemo(
      () =>
        currencyFormatter ||
        createCurrencyFormatter(currencySymbol, decimalPlaces, unidades),
      [currencyFormatter, currencySymbol, decimalPlaces, unidades],
    );
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [nodePositions, setNodePositions] = useState<NodePosition[]>([]);
    const [flowPositions, setFlowPositions] = useState<FlowPosition[]>([]);
    const [hoveredFlowIndex, setHoveredFlowIndex] = useState<number | null>(
      null,
    );
    const [tooltip, setTooltip] = useState<TooltipData | null>(null);
    // Process data dynamically from dataPoints
    const { assetItems, liabilityItems, totalAssets, totalLiabilities } =
      useMemo(() => {
        const dataPoints = data.dataPoints || [];

        // Filter by section
        const assets = dataPoints.filter(
          (point) =>
            point.section && point.section.toLowerCase().includes("activo"),
        );
        const liabilities = dataPoints.filter(
          (point) =>
            point.section &&
            (point.section.toLowerCase().includes("pasivo") ||
              point.section.toLowerCase().includes("capital")),
        );

        const assetItems = assets
          .map((point) => ({
            key: point.category,
            value: toNumber(point.valueFmt ?? point.value),
          }))
          .filter((item) => item.value > 0);

        const liabilityItems = liabilities
          .map((point) => ({
            key: point.category,
            value: toNumber(point.valueFmt ?? point.value),
          }))
          .filter((item) => item.value > 0);

        const totalAssets = assetItems.reduce(
          (sum, item) => sum + item.value,
          0,
        );
        const totalLiabilities = liabilityItems.reduce(
          (sum, item) => sum + item.value,
          0,
        );

        return { assetItems, liabilityItems, totalAssets, totalLiabilities };
      }, [data]);

    // Calculate dynamic height based on number of items
    const dynamicHeight = useMemo(() => {
      const maxItems = Math.max(assetItems.length, liabilityItems.length);
      const minHeight = 450;
      // Each item needs minimum height + gap spacing
      const minHeightPerItem = 40; // Minimum height per node
      const gapPerItem = 15; // Gap between nodes
      const topBottomPadding = 120; // Top and bottom padding for labels
      const calculatedHeight = Math.max(
        minHeight,
        maxItems * (minHeightPerItem + gapPerItem) + topBottomPadding,
      );
      return calculatedHeight;
    }, [assetItems.length, liabilityItems.length]);

    const [dimensions, setDimensions] = useState({
      width: 0,
      height: dynamicHeight,
    });

    // Expose download method
    useImperativeHandle(ref, () => ({
      downloadImage: async () => {
        if (!canvasRef.current) return;
        try {
          await downloadCanvasWithWatermark(
            canvasRef.current,
            `balance-sankey-${Date.now()}`,
          );
        } catch (err) {
          // Fallback: plain canvas download without watermark
          const link = document.createElement("a");
          link.download = `balance-sankey-${Date.now()}.png`;
          link.href = canvasRef.current.toDataURL("image/png", 1.0);
          link.click();
        }
      },
    }));

    // Initialize and handle resize
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      // Initial measurement
      const rect = container.getBoundingClientRect();
      if (rect.width > 0) {
        setDimensions({ width: rect.width, height: dynamicHeight });
      }

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width } = entry.contentRect;
          if (width > 0) {
            setDimensions({ width, height: dynamicHeight });
          }
        }
      });

      resizeObserver.observe(container);
      return () => resizeObserver.disconnect();
    }, [dynamicHeight]);

    // Main drawing effect
    useEffect(() => {
      if (!canvasRef.current || totalAssets === 0 || dimensions.width === 0)
        return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const { width, height: canvasHeight } = dimensions;
      const dpr = window.devicePixelRatio || 1;

      canvas.width = width * dpr;
      canvas.height = canvasHeight * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${canvasHeight}px`;
      ctx.scale(dpr, dpr);

      ctx.clearRect(0, 0, width, canvasHeight);

      // Scale factor for responsive layout
      const scale = width / 1200;

      // Column positions (scaled)
      const leftCol = 150 * scale;
      const centerCol = width / 2 - 15;
      const rightCol = width - 180 * scale;
      const nodeWidth = 30 * scale;
      const nodeGap = 20 * scale;

      const startY = 50; // Top margin for labels
      const bottomMargin = 50; // Bottom margin for labels
      const availableHeight = canvasHeight - startY - bottomMargin;

      // Use power scaling (0.3) to heavily compress large values
      // This gives more space to smaller items
      const powerFactor = 0.3; // Lower = more compression of large values
      const assetPowSum = assetItems.reduce(
        (sum, item) => sum + item.value ** powerFactor,
        0,
      );
      const liabPowSum = liabilityItems.reduce(
        (sum, item) => sum + item.value ** powerFactor,
        0,
      );
      const maxPowSum = Math.max(assetPowSum, liabPowSum);

      // Scale function using power scaling for aggressive compression
      const scaleValue = (v: number) => {
        const powScaled =
          (v ** powerFactor / maxPowSum) * availableHeight * 0.9;
        return Math.max(powScaled, 28); // Minimum 28px per node for visibility
      };

      const nodes: NodePosition[] = [];
      const flows: FlowPosition[] = [];

      // Calculate heights
      const assetHeights = assetItems.map((i) => scaleValue(i.value));
      const totalAssetNodesH =
        assetHeights.reduce((s, h) => s + h, 0) +
        (assetItems.length - 1) * nodeGap;

      const liabHeights = liabilityItems.map((i) => scaleValue(i.value));
      const totalLiabNodesH =
        liabHeights.reduce((s, h) => s + h, 0) +
        (liabilityItems.length - 1) * nodeGap;

      // Check if total height exceeds available space and scale down if needed
      const maxTotalHeight = Math.max(totalAssetNodesH, totalLiabNodesH);
      const scaleFactor =
        maxTotalHeight > availableHeight ? availableHeight / maxTotalHeight : 1;

      // Apply scale factor if nodes don't fit
      const scaledAssetHeights = assetHeights.map((h) => h * scaleFactor);
      const scaledLiabHeights = liabHeights.map((h) => h * scaleFactor);
      const scaledNodeGap = nodeGap * scaleFactor;

      const scaledTotalAssetH =
        scaledAssetHeights.reduce((s, h) => s + h, 0) +
        (assetItems.length - 1) * scaledNodeGap;
      const scaledTotalLiabH =
        scaledLiabHeights.reduce((s, h) => s + h, 0) +
        (liabilityItems.length - 1) * scaledNodeGap;

      // Center positions
      const centerY = startY + availableHeight / 2;

      // Position asset nodes - ensure they fit within bounds
      const assetStartY = Math.max(
        startY,
        Math.min(
          centerY - scaledTotalAssetH / 2,
          canvasHeight - bottomMargin - scaledTotalAssetH,
        ),
      );
      let assetY = assetStartY;
      const assetPositions = assetItems.map((item, i) => {
        const h = scaledAssetHeights[i];
        const pos = { ...item, y: assetY, height: h };
        assetY += h + scaledNodeGap;
        return pos;
      });

      // Position liability nodes - ensure they fit within bounds
      const liabStartY = Math.max(
        startY,
        Math.min(
          centerY - scaledTotalLiabH / 2,
          canvasHeight - bottomMargin - scaledTotalLiabH,
        ),
      );
      let liabY = liabStartY;
      const liabPositions = liabilityItems.map((item, i) => {
        const h = scaledLiabHeights[i];
        const pos = { ...item, y: liabY, height: h };
        liabY += h + scaledNodeGap;
        return pos;
      });

      // Center column total heights
      const totalAssetsH = scaleValue(totalAssets);
      const totalLiabH = scaleValue(totalLiabilities);
      const centerAssetsY = startY + (availableHeight - totalAssetsH) / 2;
      const centerLiabY = startY + (availableHeight - totalLiabH) / 2;

      // Create flows: assets → center
      let flowY = centerAssetsY;
      assetPositions.forEach((p) => {
        const fh = (p.value / totalAssets) * totalAssetsH;
        const path = new Path2D();
        const x1 = leftCol + nodeWidth;
        const y1 = p.y;
        const h1 = p.height;
        const x2 = centerCol;
        const y2 = flowY;
        const h2 = fh;

        path.moveTo(x1, y1);
        path.bezierCurveTo(
          x1 + (x2 - x1) / 2,
          y1,
          x1 + (x2 - x1) / 2,
          y2,
          x2,
          y2,
        );
        path.lineTo(x2, y2 + h2);
        path.bezierCurveTo(
          x1 + (x2 - x1) / 2,
          y2 + h2,
          x1 + (x2 - x1) / 2,
          y1 + h1,
          x1,
          y1 + h1,
        );
        path.closePath();

        flows.push({
          path,
          sourceName: p.key,
          targetName: "Total Activos",
          value: p.value,
          percentage: (p.value / totalAssets) * 100,
          color: BALANCE_COLORS.ASSETS,
        });

        flowY += fh;
      });

      // Create flows: center → liabilities
      flowY = centerLiabY;
      liabPositions.forEach((p) => {
        const fh = (p.value / totalLiabilities) * totalLiabH;
        const path = new Path2D();
        const x1 = centerCol + nodeWidth;
        const y1 = flowY;
        const h1 = fh;
        const x2 = rightCol;
        const y2 = p.y;
        const h2 = p.height;

        path.moveTo(x1, y1);
        path.bezierCurveTo(
          x1 + (x2 - x1) / 2,
          y1,
          x1 + (x2 - x1) / 2,
          y2,
          x2,
          y2,
        );
        path.lineTo(x2, y2 + h2);
        path.bezierCurveTo(
          x1 + (x2 - x1) / 2,
          y2 + h2,
          x1 + (x2 - x1) / 2,
          y1 + h1,
          x1,
          y1 + h1,
        );
        path.closePath();

        flows.push({
          path,
          sourceName: "Total Activos",
          targetName: p.key,
          value: p.value,
          percentage: (p.value / totalLiabilities) * 100,
          color: BALANCE_COLORS.LIABILITIES,
        });

        flowY += fh;
      });

      // Store node positions for hit testing
      assetPositions.forEach((p) => {
        nodes.push({
          x: leftCol,
          y: p.y,
          width: nodeWidth,
          height: p.height,
          name: p.key,
          value: p.value,
          percentage: (p.value / totalAssets) * 100,
          color: BALANCE_COLORS.ASSETS,
          side: "left",
        });
      });

      // Center node
      nodes.push({
        x: centerCol,
        y: centerAssetsY,
        width: nodeWidth,
        height: totalAssetsH,
        name: "Total Activos",
        value: totalAssets,
        percentage: 100,
        color: BALANCE_COLORS.ASSETS,
        side: "center",
      });

      liabPositions.forEach((p) => {
        nodes.push({
          x: rightCol,
          y: p.y,
          width: nodeWidth,
          height: p.height,
          name: p.key,
          value: p.value,
          percentage: (p.value / totalLiabilities) * 100,
          color: BALANCE_COLORS.LIABILITIES,
          side: "right",
        });
      });

      setNodePositions(nodes);
      setFlowPositions(flows);
    }, [
      data,
      totalAssets,
      totalLiabilities,
      dimensions,
      effectiveCurrencyFormatter,
    ]);

    // Re-render with hover effects
    useEffect(() => {
      if (!canvasRef.current || flowPositions.length === 0) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const { width, height: canvasHeight } = dimensions;
      const dpr = window.devicePixelRatio || 1;
      const scale = width / 1200;
      const nodeWidth = 30 * scale;

      ctx.clearRect(0, 0, width * dpr, canvasHeight * dpr);

      // Draw flows with hover effect
      flowPositions.forEach((flow, index) => {
        ctx.save();
        if (hoveredFlowIndex !== null) {
          ctx.globalAlpha = index === hoveredFlowIndex ? 0.65 : 0.25;
        } else {
          ctx.globalAlpha = 0.4;
        }
        ctx.fillStyle = flow.color;
        ctx.fill(flow.path);
        ctx.restore();
      });

      // Draw nodes
      nodePositions.forEach((node) => {
        ctx.fillStyle = node.color;
        ctx.fillRect(node.x, node.y, node.width, node.height);

        // Labels
        const fontSize = Math.max(11, 14 * scale);
        const smallFontSize = Math.max(10, 12 * scale);

        let textX: number;
        let align: CanvasTextAlign;

        if (node.side === "left") {
          textX = node.x + nodeWidth + 12 * scale;
          align = "left";
        } else if (node.side === "right") {
          textX = node.x - 12 * scale;
          align = "right";
        } else {
          textX = node.x + nodeWidth / 2;
          align = "center";
        }

        ctx.textAlign = align;
        ctx.fillStyle = "#1e293b";
        ctx.font = `600 ${fontSize}px "Open Sans", sans-serif`;
        ctx.fillText(node.name, textX, node.y + node.height / 2 - 4);

        ctx.fillStyle = "#64748b";
        ctx.font = `400 ${smallFontSize}px "Open Sans", sans-serif`;
        const valueText =
          node.side === "center"
            ? effectiveCurrencyFormatter(node.value)
            : `${effectiveCurrencyFormatter(
                node.value,
              )} (${formatNumberWithSeparators(node.percentage, 1)}%)`;
        ctx.fillText(valueText, textX, node.y + node.height / 2 + 12);
      });
    }, [
      hoveredFlowIndex,
      flowPositions,
      nodePositions,
      dimensions,
      effectiveCurrencyFormatter,
    ]);

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current || !containerRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const dpr = window.devicePixelRatio || 1;
        const x = ((e.clientX - rect.left) * scaleX) / dpr;
        const y = ((e.clientY - rect.top) * scaleY) / dpr;

        // Check nodes first
        const hoveredNode = nodePositions.find(
          (node) =>
            x >= node.x &&
            x <= node.x + node.width &&
            y >= node.y &&
            y <= node.y + node.height,
        );

        if (hoveredNode) {
          setHoveredFlowIndex(null);
          setTooltip({
            x:
              e.clientX -
              containerRef.current.getBoundingClientRect().left +
              15,
            y:
              e.clientY - containerRef.current.getBoundingClientRect().top - 10,
            name: hoveredNode.name,
            value: effectiveCurrencyFormatter(hoveredNode.value),
            percentage: `${formatNumberWithSeparators(
              hoveredNode.percentage,
              1,
            )}%`,
            color: hoveredNode.color,
          });
          canvas.style.cursor = "pointer";
          return;
        }

        // Check flows
        const hoveredFlowIdx = flowPositions.findIndex((flow) => {
          return ctx.isPointInPath(
            flow.path,
            x * (window.devicePixelRatio || 1),
            y * (window.devicePixelRatio || 1),
          );
        });

        if (hoveredFlowIdx !== -1) {
          const hoveredFlow = flowPositions[hoveredFlowIdx];
          setHoveredFlowIndex(hoveredFlowIdx);
          setTooltip({
            x:
              e.clientX -
              containerRef.current.getBoundingClientRect().left +
              15,
            y:
              e.clientY - containerRef.current.getBoundingClientRect().top - 10,
            name: `${hoveredFlow.sourceName} → ${hoveredFlow.targetName}`,
            value: effectiveCurrencyFormatter(hoveredFlow.value),
            percentage: `${formatNumberWithSeparators(
              hoveredFlow.percentage,
              1,
            )}%`,
            color: hoveredFlow.color,
            isFlow: true,
          });
          canvas.style.cursor = "pointer";
          return;
        }

        setHoveredFlowIndex(null);
        setTooltip(null);
        canvas.style.cursor = "default";
      },
      [nodePositions, flowPositions, effectiveCurrencyFormatter],
    );

    const handleMouseLeave = useCallback(() => {
      setHoveredFlowIndex(null);
      setTooltip(null);
    }, []);

    if (totalAssets === 0) {
      return (
        <ChartContainer ref={containerRef}>
          <Box
            sx={{
              height: 400,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              Ingrese datos del balance general para visualizar
            </Typography>
          </Box>
        </ChartContainer>
      );
    }

    return (
      <ChartContainer ref={containerRef}>
        {divisa && <DivisaBadge>{divisa}</DivisaBadge>}
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{
            width: "100%",
            height: `${dynamicHeight}px`,
            display: "block",
          }}
        />
        <Tooltip
          visible={!!tooltip}
          sx={{
            left: tooltip?.x || 0,
            top: tooltip?.y || 0,
          }}
        >
          {tooltip && (
            <>
              <TooltipLabel>{tooltip.name}</TooltipLabel>
              <TooltipValue>
                <TooltipColorDot color={tooltip.color} />
                <span style={{ color: "#64748b" }}>Valor:</span>
                <span style={{ fontWeight: 600 }}>{tooltip.value}</span>
              </TooltipValue>
            </>
          )}
        </Tooltip>
      </ChartContainer>
    );
  },
);

BalanceSankey.displayName = "BalanceSankey";

export default BalanceSankey;
